import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  InputBase,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { SEARCH_DEBOUNCE_TIME } from '@constants/constants';
import useDebounce from '@hooks/useDebounce';
import { GenericTable } from '@components';
import { PaginationConfig } from '@utils/types/PaginationConfig';
import Pagination from '../../Dashboard/components/Pagination';
import CustomButton from '../../../components/CustomButton/CustomButton';
import ReferrerSetupForm, { ReferrerListItem } from './components/ReferrerSetupForm';
import { useLazyGetReferrersQuery } from '@api/referrers';

const referrersTableMetadata = {
  chartType: 'TABLE',
  columns: [
    {
      id: 'email',
      name: 'Email',
      type: 'string',
    },
    {
      id: 'name',
      name: 'Name',
      type: 'string',
    },
    {
      id: 'type',
      name: 'Type',
      type: 'string',
    },
    {
      id: 'numberOfCohorts',
      name: 'Number of Cohorts',
      type: 'number',
    },
    {
      id: 'status',
      name: 'Status',
      type: 'string',
    },
    {
      id: 'activeDealCount',
      name: 'Active Deals',
      type: 'number',
    },
  ],
};

const referrersTableMappedColumns = [
  {
    key: 'email',
    label: 'Email',
  },
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'type',
    label: 'Type',
  },
  {
    key: 'numberOfCohorts',
    label: 'Number of Cohorts',
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'activeDealCount',
    label: 'Active Deals',
  },
];

const REFERRER_TYPE_OPTIONS = {
  PERPETUAL: 'Perpetual',
  COHORT_BASED: 'Cohort Based',
};

const PAGE_SIZE = 10;

const ReferrerSetup: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const [showReferrerEdit, setShowReferrerEdit] = useState<boolean>(false);
  const [showReferrerCreate, setShowReferrerCreate] = useState<boolean>(false);
  const [referrers, setReferrers] = useState<ReferrerListItem[] | null>(null);
  const [selectedReferrerId, setSelectedReferrerId] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [referrersPagination, setReferrersPagination] = useState<PaginationConfig>({
    pageNumber: 1,
    pageSize: PAGE_SIZE,
    total: 0,
  });

  const [getReferrers, { isLoading: isReferrerLoading }] = useLazyGetReferrersQuery();

  // Hook to fetch referrers initially
  useEffect(() => {
    fetchReferrers({ pageNumber: 1, search: searchValue });
  }, []);

  // Function to call API to fetch referrers with search
  const fetchReferrers = async (payload: { pageNumber: number; search?: string }) => {
    try {
      const response = await getReferrers({
        search: payload?.search || '',
        pageNumber: payload.pageNumber - 1,
        pageSize: referrersPagination.pageSize,
      }).unwrap();

      if (response?.data?.items?.length > 0) {
        const formattedReferrers: ReferrerListItem[] = response.data.items.map((r: any) => ({
          id: r.referrerId,
          email: r.email,
          name: r.username,
          type: r.type,
          status: r.status,
          numberOfCohorts: r.numberOfCohorts,
          activeDealCount: r.activeDealCount,
        }));
        setReferrers(formattedReferrers);

        // Update pagination with response metadata
        setReferrersPagination({
          pageNumber: payload.pageNumber,
          pageSize: referrersPagination.pageSize,
          total: response.data.totalElements || 0,
        });
      } else {
        setReferrers([]);
        setReferrersPagination({
          ...referrersPagination,
          total: 0,
        });
      }
    } catch (error) {
      console.error('Error fetching referrers:', error);
      setReferrers([]);
      setReferrersPagination({
        ...referrersPagination,
        total: 0,
      });
    }
  };

  // Handle close referrer form
  const handleCloseReferrerDetails = () => {
    setShowReferrerEdit(false);
    setShowReferrerCreate(false);
    setSelectedReferrerId(null);
    fetchReferrers({ pageNumber: 1, search: searchValue });
  };

  // Handle open edit referrer form
  const handleEditReferrerButtonClick = (referrerId: string) => {
    setSelectedReferrerId(referrerId);
    setShowReferrerEdit(true);
  };

  // Handle open new referrer form
  const handleNewReferrerButtonClick = () => {
    setSelectedReferrerId(null);
    setShowReferrerCreate(true);
  };

  // Handle search referrer list by email and name
  const debouncedSearch = useDebounce(
    (value: string) => fetchReferrers({ pageNumber: 1, search: value }),
    SEARCH_DEBOUNCE_TIME,
  );

  // Handle search input value change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const containerStyles = {
    display: 'flex',
    justifyContent: isMobile ? 'center' : 'space-between',
    alignItems: isMobile ? 'flex-end' : 'center',
    pb: '1rem',
    flexDirection: isMobile ? 'column' : 'row',
  };

  const searchContainerStyles = {
    flex: 1,
    width: isMobile ? '100%' : '80%',
  };

  const searchPaperStyles = {
    height: 45,
    px: '19px',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '28px',
    backgroundColor: theme.palette.custom_field_background,
    width: isMobile ? '100%' : '80%',
    boxShadow: 'none',
  };

  const inputStyles = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  };

  // Conditionally mapped columns
  const conditionallyMappedColumn = referrersTableMappedColumns.map((col: any) => {
    if (col.key === 'type') {
      return {
        ...col,
        render: (key: string, row: any) => {
          const value = row[col.key];
          return (
            <Box sx={{ display: 'flex', justifyContent: 'left' }}>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: '#1B1C17',
                }}
              >
                {REFERRER_TYPE_OPTIONS[value as keyof typeof REFERRER_TYPE_OPTIONS]}
              </Typography>
            </Box>
          );
        },
      };
    }
    if (col.key === 'numberOfCohorts') {
      return {
        ...col,
        render: (key: string, row: any) => {
          const value = row[col.key];
          const type = row.type;
          return (
            <Box sx={{ display: 'flex', justifyContent: 'right' }}>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: '#1B1C17',
                }}
              >
                {type === 'COHORT_BASED' ? value : '-'}
              </Typography>
            </Box>
          );
        },
      };
    }
    if (col.key === 'activeDealCount') {
      return {
        ...col,
        render: (key: string, row: any) => {
          const value = row[col.key];
          return (
            <Box sx={{ display: 'flex', justifyContent: 'right' }}>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: '#1B1C17',
                }}
              >
                {value}
              </Typography>
            </Box>
          );
        },
      };
    }
    if (col.key === 'status') {
      return {
        ...col,
        render: (key: string, row: any) => {
          const value = row[col.key];
          return (
            <Box sx={{ display: 'flex', justifyContent: 'left' }}>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: '#1B1C17',
                }}
              >
                {value === 'INACTIVE' ? 'Inactive' : 'Active'}
              </Typography>
            </Box>
          );
        },
      };
    }
    return col;
  });

  // Search Component
  const renderSearchIcon = () => {
    if (searchValue) {
      return (
        <IconButton
          size="small"
          onClick={() =>
            handleSearchInputChange({
              target: { value: '' },
            } as React.ChangeEvent<HTMLInputElement>)
          }
          sx={{ p: 0.5 }}
          aria-label="Clear search"
        >
          <CloseIcon />
        </IconButton>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <SearchIcon />
      </Box>
    );
  };

  // Get selected referrer
  const getSelectedReferrer = () => {
    return referrers?.find((r) => r.id === selectedReferrerId) || null;
  };

  return (
    <>
      {/* {(isReferrerLoading && referrers?.length === 0) && <FullPageLoader hasSideNav={true} />} */}
      <Box
        id="pvx-referrer-details-container"
        style={{
          width: '100%',
          height: '100vh',
          border: 'none',
          overflow: 'auto',
          paddingLeft: '2rem',
          paddingRight: '2rem',
          paddingTop: '1rem',
          paddingBottom: '1rem',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '0.5rem',
            marginTop: '1.3rem',
            marginBottom: '2rem',
          }}
        >
          <Typography
            fontSize="2rem"
            fontWeight="600"
            lineHeight="1.5rem"
            fontFamily="Manrope,sans-serif"
            sx={{ color: '#1B1C17' }}
          >
            Referrers
          </Typography>
        </Box>
        <Box sx={containerStyles}>
          <Box sx={searchContainerStyles}>
            <Paper component="form" sx={searchPaperStyles}>
              <InputBase
                inputRef={searchInputRef}
                placeholder="Search by Email"
                value={searchValue}
                onChange={handleSearchInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                sx={inputStyles}
                fullWidth
              />
              {renderSearchIcon()}
            </Paper>
          </Box>

          <CustomButton
            color="#F6F8F9"
            backgroundColor="#2F736E"
            name="New Referrer"
            startIcon={AddIcon}
            onClick={handleNewReferrerButtonClick}
            height="40px"
          />
        </Box>
        {showReferrerCreate || (showReferrerEdit && selectedReferrerId) ? (
          <ReferrerSetupForm
            referrer={getSelectedReferrer()}
            handleCloseReferrerDetails={handleCloseReferrerDetails}
          />
        ) : (
          <Box
            key={'All Referrers'}
            sx={{
              width: '100%',
              height: 'fit-content',
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '0.75rem',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                marginBottom: '1rem',
              }}
            >
              <Typography
                sx={{
                  fontSize: '1rem',
                  lineHeight: '1.375rem',
                  fontWeight: 700,
                  color: '#162936',
                }}
              >
                Referrers
              </Typography>
              <Stack direction={'row'} gap={'0.5rem'}>
                <Pagination
                  page={referrersPagination.pageNumber}
                  size={referrersPagination.pageSize}
                  total={referrersPagination.total}
                  handleChange={(pageNumber: number) =>
                    fetchReferrers({
                      pageNumber,
                      search: searchValue,
                    })
                  }
                  fileName={''}
                  chartId={''}
                  submoduleId={''}
                  filterSelection={{}}
                />
                <IconButton
                  onClick={() =>
                    fetchReferrers({
                      pageNumber: 1,
                      search: searchValue,
                    })
                  }
                  sx={{ color: '#2E736E' }}
                >
                  <RefreshIcon />
                </IconButton>
              </Stack>
            </Box>
            <GenericTable
              data={referrers || []}
              columns={conditionallyMappedColumn}
              meta={referrersTableMetadata}
              actions={[
                {
                  icon: 'edit-icon-green.svg',
                  action: (id) => handleEditReferrerButtonClick(id),
                },
              ]}
              isLoading={isReferrerLoading}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default ReferrerSetup;
